/**
 * V2.0.48 — ADA Audit flyout.
 *
 * Lists every door whose width is below its ADA target. Each row has
 * an "Apply" button that bumps just that door (snapshotting the
 * original on `_pre_ada_width` so the global Accessible Mode toggle's
 * OFF restore still works correctly).
 */
import React, { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";
import { Accessibility, ChevronRight, AlertOctagon, CheckCircle2 } from "lucide-react";

export default function AdaAuditDialog({ open, onOpenChange, audit, onApplyDoor, onApplyAll }) {
  const [applyingId, setApplyingId] = useState(null);
  const [applyingAll, setApplyingAll] = useState(false);
  const violations = audit?.violations || [];

  const handleApply = async (doorId) => {
    setApplyingId(doorId);
    try {
      await onApplyDoor?.(doorId);
    } finally {
      setApplyingId(null);
    }
  };

  const handleApplyAll = async () => {
    setApplyingAll(true);
    try {
      await onApplyAll?.();
    } finally {
      setApplyingAll(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid="ada-audit-dialog"
        className="max-w-md bg-zinc-950 border-violet-400/30"
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-violet-100">
            <Accessibility className="w-5 h-5" />
            ADA Door Audit
          </DialogTitle>
          <DialogDescription className="text-zinc-400 text-xs leading-relaxed">
            Per ICC A117.1 — every doorway should provide ≥ 32″ clear width
            (≥ 36″ for primary bedroom + bathroom). Bump individual doors
            without flipping the global Accessible Mode toggle.
          </DialogDescription>
        </DialogHeader>

        {violations.length === 0 ? (
          <div
            data-testid="ada-audit-empty"
            className="flex items-center gap-2 px-3 py-4 rounded border border-emerald-400/30 bg-emerald-500/10 text-emerald-200"
          >
            <CheckCircle2 className="w-4 h-4" />
            <span className="text-sm">All doors meet ADA spec — nice work.</span>
          </div>
        ) : (
          <>
            <div className="max-h-72 overflow-y-auto -mx-2 px-2 space-y-1.5">
              {violations.map((v, idx) => {
                const inchesCur = (v.current * 39.3701).toFixed(1);
                const inchesTgt = Math.round(v.target * 39.3701);
                const isApplying = applyingId === v.door_id;
                return (
                  <div
                    key={v.door_id || `idx-${idx}`}
                    data-testid={`ada-audit-row-${v.door_id || "unknown"}`}
                    className="flex items-center justify-between gap-2 px-3 py-2 rounded border border-zinc-800 bg-zinc-900/60 hover:border-violet-400/30 transition-colors"
                  >
                    <div className="min-w-0">
                      <div className="text-zinc-100 text-sm font-medium truncate">
                        {v.door_id || "Door"}
                        <span className="text-zinc-500 font-normal ml-1.5">
                          {v.room_name ? `· ${v.room_name}` : ""}
                        </span>
                      </div>
                      <div className="font-mono text-[10px] text-zinc-400 mt-0.5">
                        {inchesCur}″ <ChevronRight className="inline w-3 h-3 mx-0.5 text-violet-400" />
                        <span className="text-violet-300">{inchesTgt}″ ADA</span>
                      </div>
                    </div>
                    <button
                      type="button"
                      data-testid={`ada-audit-apply-${v.door_id || "unknown"}`}
                      disabled={isApplying || applyingAll}
                      onClick={() => handleApply(v.door_id)}
                      className="shrink-0 px-2.5 py-1 rounded-full border border-violet-400/40 bg-violet-500/10 hover:bg-violet-500/25 text-violet-100 text-[10px] font-mono uppercase tracking-widest disabled:opacity-50 transition-colors"
                    >
                      {isApplying ? "…" : `Apply ${inchesTgt}″`}
                    </button>
                  </div>
                );
              })}
            </div>

            <div className="flex items-center justify-between gap-2 pt-2 border-t border-zinc-800 mt-2">
              <div className="flex items-center gap-1.5 text-[10px] text-violet-300 font-mono uppercase tracking-widest">
                <AlertOctagon className="w-3 h-3" />
                {violations.length} below spec
              </div>
              <button
                type="button"
                data-testid="ada-audit-apply-all"
                disabled={applyingAll}
                onClick={handleApplyAll}
                className="px-3 py-1.5 rounded-full bg-gradient-to-r from-indigo-600 to-violet-600 hover:from-indigo-500 hover:to-violet-500 text-white text-[10px] font-mono uppercase tracking-widest disabled:opacity-50 transition-colors"
              >
                {applyingAll ? "Applying…" : `Apply All (${violations.length})`}
              </button>
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
